import React from 'react';
import { Player, Specialization, LEVELS } from '../types';
import { PlayerRow } from './PlayerRow';

interface PlayerTableProps {
  players: Player[];
  specializations: Specialization[];
  onPlayerUpdate: (playerId: string, updatedPlayer: Player) => void;
  onPlayerAdd: () => void;
  onPlayerDelete: (playerId: string) => void;
  getSpecializationsForLevel: (level: number) => Specialization[];
}

export const PlayerTable: React.FC<PlayerTableProps> = ({
  players,
  specializations,
  onPlayerUpdate,
  onPlayerAdd,
  onPlayerDelete,
  getSpecializationsForLevel
}) => {
  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 p-4 border-b flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">
          Once Human - Specialization Tracker
        </h2>
        <button
          onClick={onPlayerAdd}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <span>+</span>
          Add Player
        </button>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 p-3 border-b">
        <div className="text-sm text-gray-600 mb-2">Color coding by category:</div>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span>Building</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span>Crafting</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-500 rounded"></div>
            <span>Gathering</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-purple-500 rounded"></div>
            <span>Management</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left border-r sticky left-0 bg-gray-100 z-20 min-w-[200px]">
                Player
              </th>
              {LEVELS.map((level) => {
                const specsCount = getSpecializationsForLevel(level).length;
                return (
                  <th 
                    key={level} 
                    className="p-3 text-center border-r min-w-[200px]"
                  >
                    <div className="font-bold">Level {level}</div>
                    <div className="text-xs text-gray-500 font-normal">
                      {specsCount} specs available
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {players.length === 0 ? (
              <tr>
                <td 
                  colSpan={LEVELS.length + 1} 
                  className="p-8 text-center text-gray-500"
                >
                  No players added yet. Click "Add Player" to get started!
                </td>
              </tr>
            ) : (
              players.map((player) => (
                <PlayerRow
                  key={player.id}
                  player={player}
                  specializations={specializations}
                  onPlayerUpdate={onPlayerUpdate}
                  onPlayerDelete={onPlayerDelete}
                  getSpecializationsForLevel={getSpecializationsForLevel}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with stats */}
      <div className="bg-gray-50 p-3 border-t text-sm text-gray-600">
        <div className="flex justify-between">
          <span>{players.length} player(s)</span>
          <span>{specializations.length} total specializations loaded</span>
        </div>
      </div>
    </div>
  );
};